import { creatorbase, creatorbaseQueries } from '@round/api';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdatePost } from '@round/creatorbase-helpers/api-hooks';
import { showNotification } from 'helpers';
import useRefreshProjectCampaignStats from '../useRefreshProjectCampaignStats';
import { getProjectDetailsPostsParams } from './getProjectDetailsPostsParams';

type OptimisticContext = { previousPosts: creatorbase.Post[] | undefined };

// Inline cells patch nested detail objects (e.g. instagram_details:
// { story_view_count }), so merge those into the existing details instead of
// letting a top-level spread replace the whole object and blank the sibling
// fields other cells read from cache until onSettled refetches.
function applyPatch(post: creatorbase.Post, patch: Partial<creatorbase.PatchPostData>): creatorbase.Post {
    const merged = { ...post, ...patch };

    if ('instagram_details' in patch) {
        merged.instagram_details = { ...post.instagram_details, ...patch.instagram_details };
    }

    if ('tiktok_details' in patch) {
        merged.tiktok_details = { ...post.tiktok_details, ...patch.tiktok_details };
    }

    return merged as creatorbase.Post;
}

export default function useUpdateProjectDetailsPost(campaignId: number, platform: creatorbase.Platform) {
    const queryClient = useQueryClient();
    const refreshAllStats = useRefreshProjectCampaignStats();
    const listKey = creatorbaseQueries.posts.keys.list(getProjectDetailsPostsParams(campaignId));

    const { mutateAsync, isLoading } = useUpdatePost<OptimisticContext>({
        onMutate: async ({ id, ...patch }) => {
            await queryClient.cancelQueries({ queryKey: listKey });
            const previousPosts = queryClient.getQueryData<creatorbase.Post[]>(listKey);

            queryClient.setQueryData<creatorbase.Post[]>(
                listKey,
                (posts) => posts?.map((post) => (post.id === id ? applyPatch(post, patch) : post))
            );

            return { previousPosts };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousPosts) {
                queryClient.setQueryData(listKey, context.previousPosts);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: listKey });
            refreshAllStats(campaignId, platform).catch(() => {
                showNotification('Could not refresh data', 'error');
            });
        },
    });

    // Present the mutation as patchPost's response-returning contract so the post
    // tables' existing updatePost meta — the inline cells and column editors —
    // keep working unchanged while gaining the optimistic patch and stats
    // cascade. Expected error responses are unwrapped back into the response so
    // callers can still branch on status; anything else propagates.
    const updatePost: typeof creatorbase.patchPost = useCallback(
        async (postId, data) => {
            try {
                const updatedPost = await mutateAsync({ id: postId, ...data });
                return { status: 200, data: updatedPost };
            } catch (error) {
                if (error instanceof Error && creatorbaseQueries.posts.utils.update.isExpectedErrorResponse(error)) {
                    return error.response;
                }

                throw error;
            }
        },
        [mutateAsync]
    );

    return { updatePost, isLoading };
}
